import { useEffect, useState } from 'react';
import { Button } from '~/components/ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog'
import { Alert } from '~/components/ui/alert';
import { AlertDialogDescription } from '@radix-ui/react-alert-dialog';
import { Loader2 } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const SignOutConfirmModal = ({ isOpen, onClose }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`/api/auth/signout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      setIsLoading(false);
      if (!response.ok) {
        console.error('Sign out failed:', await response.text());
        setError(await response.text());
      } else {
        console.log('Signed out successfully');
        window.location.href = '/';
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Unexpected error occurred during sign out:', error);
      setError('An unexpected error occurred during sign out');
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign Out</AlertDialogTitle>
            <AlertDialogDescription className='text-sm text-gray-500'>
              Are you sure you want to sign out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <div className='mb-4'>
              <Alert variant={'destructive'}>{error}</Alert>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              className='min-w-20'
              onClick={onClose}
            >
              Close
            </AlertDialogCancel>
            <Button
              onClick={async () => await handleSignOut()}
              disabled={isLoading}
              className='w-24'
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign Out'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};